import { Container, IDestroyOptions, Matrix, Rectangle, Texture } from "pixi.js"
import { SoftenedGraphics } from "./softened_graphics"
import { polygonsKey, ShadowPolygon } from "./building_shadow_geometry"
import { PlantSilhouette, ShadowStamp, stampQuad, stampsKey } from "./foliage_shadow_geometry"

/**
 * Live tuning, like window.occluderFade: window.buildingShadow.enabled / .alpha / .indoors / .softness.
 * `indoors: false` keeps floors under a roof (or under the storey above) lit. Setting
 * window.buildingShadow = false turns the shadows off.
 */
export const BUILDING_SHADOW = { enabled: true, alpha: 0.3, indoors: true, softness: 1.25 }

export const buildingShadowConfig = (): typeof BUILDING_SHADOW => {
    if (typeof window === "undefined") return BUILDING_SHADOW
    const w = window as any
    if (w.buildingShadow === undefined) w.buildingShadow = BUILDING_SHADOW
    const live = w.buildingShadow
    if (live === false) return { ...BUILDING_SHADOW, enabled: false }
    return live && typeof live === "object" ? live : BUILDING_SHADOW
}

/** Above every ground tile layer (zIndex 0), below every sorted sprite and body (100 and up). */
export const BUILDING_SHADOW_Z_INDEX = 1

/** How strongly shadows show at a room's brightness: full by day, gone by night and underground. */
export const daylightFactor = (brightness: number | undefined): number =>
    brightness === undefined ? 1 : Math.min(1, Math.max(0, (brightness - 0.4) / 0.6))

/**
 * Polygons that overlap or nearly touch, grouped, so each building bakes into its own small texture
 * rather than one texture spanning every building in the room.
 */
export const groupPolygons = (polygons: readonly ShadowPolygon[], pad = 8, bucket = 64): ShadowPolygon[][] => {
    const parent = polygons.map((_, i) => i)
    const find = (i: number): number => (parent[i] === i ? i : (parent[i] = find(parent[i])))
    const owner = new Map<string, number>()
    polygons.forEach((polygon, i) => {
        let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity
        for (let k = 0; k < polygon.length; k += 2) {
            minX = Math.min(minX, polygon[k]); maxX = Math.max(maxX, polygon[k])
            minY = Math.min(minY, polygon[k + 1]); maxY = Math.max(maxY, polygon[k + 1])
        }
        for (let bx = Math.floor((minX - pad) / bucket); bx <= Math.floor((maxX + pad) / bucket); bx++) {
            for (let by = Math.floor((minY - pad) / bucket); by <= Math.floor((maxY + pad) / bucket); by++) {
                const key = `${bx},${by}`
                const other = owner.get(key)
                if (other === undefined) owner.set(key, i)
                else parent[find(i)] = find(other)
            }
        }
    })
    const groups = new Map<number, ShadowPolygon[]>()
    polygons.forEach((polygon, i) => {
        const root = find(i)
        const group = groups.get(root)
        if (group) group.push(polygon)
        else groups.set(root, [polygon])
    })
    return [...groups.values()]
}

/** A plant's silhouette as a black picture, one texture per plant, and a view of it per run of rows. */
const silhouettes = new WeakMap<PlantSilhouette, { texture: Texture; rows: Map<string, Texture> }>()
const stampTexture = ({ art, y, height }: ShadowStamp): Texture => {
    let made = silhouettes.get(art)
    if (!made) {
        const rgba = new Uint8Array(art.width * art.height * 4)
        for (let i = 0; i < art.alpha.length; i++) rgba[i * 4 + 3] = art.alpha[i]
        made = { texture: Texture.fromBuffer(rgba, art.width, art.height), rows: new Map() }
        silhouettes.set(art, made)
    }
    const key = `${y}:${height}`
    let rows = made.rows.get(key)
    if (!rows) made.rows.set(key, rows = new Texture(made.texture.baseTexture, new Rectangle(0, y, art.width, height)))
    return rows
}

/**
 * One storey's sun shadows: every polygon filled solid and every stamp (a plant's silhouette drawn
 * black through its map, foliage_shadow_geometry.ts) drawn with them, softened and baked once per
 * change (SoftenedGraphics), and shown through the layer's alpha so overlapping shadows never darken
 * twice. No live filter, mask or extra pass per frame.
 *
 * A group whose shape is unchanged keeps its bake: a wall built in a clearing re-bakes only its own
 * building, never the forest's shadow beside it (foliage_shadow.ts), which grows as wide as the room.
 */
export class BuildingShadowLayer extends Container {
    private groups: { key: string; graphics: SoftenedGraphics }[] = []
    /** How many groups the last draw baked afresh (the rest kept their bake). */
    lastBaked = 0

    constructor() {
        super()
        this.zIndex = BUILDING_SHADOW_Z_INDEX
        this.interactive = false
        this.interactiveChildren = false
    }

    draw(polygons: readonly ShadowPolygon[], softness: number, stamps: readonly ShadowStamp[] = []): void {
        const previous = new Map(this.groups.map(group => [group.key, group]))
        this.groups = []
        this.lastBaked = 0
        // A stamp is grouped by the quadrilateral it lands on.
        const quads = new Map(stamps.map(stamp => [stampQuad(stamp), stamp]))
        groupPolygons([...polygons, ...quads.keys()]).forEach(members => {
            const group = members.filter(polygon => !quads.has(polygon))
            const groupStamps = members.filter(polygon => quads.has(polygon)).map(quad => quads.get(quad))
            const key = `${softness}|${polygonsKey(group)}|${stampsKey(groupStamps)}`
            const kept = previous.get(key)
            if (kept) {
                previous.delete(key)
                this.groups.push(kept)
                return
            }
            const graphics = new SoftenedGraphics(softness, 2)
            if (group.length) {
                graphics.beginFill(0x000000, 1)
                group.forEach(polygon => graphics.drawPolygon(polygon))
                graphics.endFill()
            }
            groupStamps.forEach(stamp => graphics.stamp(stampTexture(stamp), new Matrix(...stamp.matrix)))
            this.addChild(graphics)
            graphics.soften()
            this.groups.push({ key, graphics })
            this.lastBaked++
        })
        previous.forEach(({ graphics }) => this.destroyGraphics(graphics))
    }

    get groupCount(): number { return this.groups.length }

    private destroyGraphics(graphics: SoftenedGraphics): void {
        graphics.parent?.removeChild(graphics)
        graphics.destroy()
    }

    private clearGroups(): void {
        this.groups.forEach(({ graphics }) => this.destroyGraphics(graphics))
        this.groups = []
    }

    destroy(options?: IDestroyOptions | boolean): void {
        this.clearGroups()
        super.destroy(options)
    }
}
